import { useState } from 'react'
import { useQuery, useMutation, usePaginatedQuery } from 'convex/react'
import { api } from '../../../convex/_generated/api'
import { useAuth, usePermissions } from '@/hooks/useAuth'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Loading } from '@/components/ui/loading'
import {
  Users,
  Search,
  Edit,
  Trash2,
  UserCheck,
  UserX,
  Key,
  Plus,
  AlertTriangle,
  X,
  UserPlus,
  ChevronDown,
  Loader2
} from 'lucide-react'
import { Link } from 'react-router-dom'
import {Id} from "../../../convex/_generated/dataModel";

const ITEMS_PER_PAGE = 10

function readableMutationError(error: unknown): string {
  if (!(error instanceof Error)) return 'Errore durante la creazione dell’utente mock'
  const uncaught = error.message.match(/Uncaught Error:\s*([^\n]+)/i)?.[1]?.trim()
  if (uncaught) return uncaught
  return error.message
    .replace(/^\[CONVEX[^\]]*\]\s*/i, '')
    .replace(/\s+Called by client[\s\S]*$/i, '')
    .trim() || 'Errore durante la creazione dell’utente mock'
}

function getUserCompanyIds(user: {
  company_id?: string
  company_ids?: string[]
}): string[] {
  return Array.from(
    new Set(
      [
        ...(Array.isArray(user.company_ids) ? user.company_ids : []),
        user.company_id,
      ]
        .filter(Boolean)
        .map(String)
    )
  )
}

export default function UserList() {
  const { profile } = useAuth()
  const { hasPermission } = usePermissions()
  const sessionInfo = useQuery(api.profiles.getSessionInfo)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [roleFilter, setRoleFilter] = useState('')
  const [companyFilter, setCompanyFilter] = useState('')
  const [showMockForm, setShowMockForm] = useState(false)
  const [mockFormData, setMockFormData] = useState({
    nome: '',
    cognome: '',
    ruolo: 'dentista' as 'clinic_owner' | 'dentista' | 'aso' | 'staff',
    company_id: ''
  })
  const [mockFormError, setMockFormError] = useState('')
  const [mockSubmitting, setMockSubmitting] = useState(false)

  // Check permissions
  const canManageUsers = hasPermission('manage_users')
  const canManageApiKeys = hasPermission('manage_api_keys')
  const isDirectAdmin =
    sessionInfo?.ruolo === 'amministratore' &&
    !sessionInfo.impersonating_profile_id

  // Get users with pagination
  const {
    results: users,
    status: usersStatus,
    loadMore: loadMoreUsers
  } = usePaginatedQuery(
    api.profiles.listProfiles,
    canManageUsers && profile
      ? {
          viewerKey: profile._id,
          ...(companyFilter
            ? { company_id: companyFilter as Id<'companies'> }
            : {}),
        }
      : "skip",
    { initialNumItems: ITEMS_PER_PAGE }
  )

  const hasActiveFilters = Boolean(searchTerm || statusFilter || roleFilter || companyFilter)

  // Full dataset for accurate search/filtering and stats
  const allUsers = useQuery(
    api.profiles.listProfiles,
    canManageUsers && profile
      ? {
          viewerKey: profile._id,
          ...(companyFilter
            ? { company_id: companyFilter as Id<'companies'> }
            : {}),
        }
      : "skip"
  )

  // Get companies (not paginated - usually few items)
  const companies = useQuery(
    api.companies.listCompanies,
    canManageUsers ? {} : "skip"
  )

  // Delete user mutation
  const deleteUser = useMutation(api.profiles.deleteProfile)
  const toggleUserStatus = useMutation(api.profiles.updateProfile)
  
  // API key mutations
  const deactivateUserApiKey = useMutation(api.apiKeys.deactivateUserApiKey)
  
  // Create mock profile mutation
  const createMockProfile = useMutation(api.profiles.createMockProfile)

  if (!canManageUsers) {
    return (
      <Card className="shadow-none">
        <CardContent className="p-8 text-center">
          <Users className="mx-auto mb-4 h-12 w-12 text-gray-400" aria-hidden="true" />
          <h2 className="mb-2 text-xl font-semibold">Accesso Negato</h2>
          <p className="text-gray-600">Non hai i permessi per gestire gli utenti.</p>
        </CardContent>
      </Card>
    )
  }

  if (usersStatus === 'LoadingFirstPage') {
    return <Loading />
  }

  if (allUsers === undefined) {
    return <Loading />
  }

  // Create company lookup
  const companyRows = Array.isArray(companies) ? companies : (companies?.page ?? [])
  const companyLookup = companyRows.reduce((acc, company) => {
    acc[company._id] = company
    return acc
  }, {} as Record<string, any>) || {}

  const baseUsers = hasActiveFilters ? (allUsers as any[] | undefined) : users

  // Filter users based on search and filters
  const filteredUsers = baseUsers?.filter(user => {
    const matchesSearch =
      user.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.cognome.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase())

    const matchesStatus = !statusFilter ||
      (statusFilter === 'active' && user.active && !user.is_deleted) ||
      (statusFilter === 'inactive' && (!user.active || user.is_deleted))

    const matchesRole = !roleFilter || user.ruolo === roleFilter

    const companyIds = getUserCompanyIds(user)
    const matchesCompany = !companyFilter || companyIds.includes(companyFilter)

    return matchesSearch && matchesStatus && matchesRole && matchesCompany
  }) || []

  // Check if user is mock (user_id === _id)
  const isMockUser = (user: any) => {
    return user.user_id === user._id.toString()
  }

  const isPendingAuth0User = (user: any) => !user.user_id

  const companyOptions = [...companyRows].sort((a, b) =>
    a.nome.localeCompare(b.nome, 'it')
  )

  const handleDeleteUser = async (userId: string) => {
    if (window.confirm('Sei sicuro di voler eliminare questo utente?')) {
      try {
        await deleteUser({ profileId: userId })
      } catch (error) {
        console.error('Errore eliminazione utente:', error)
        alert('Errore durante l\'eliminazione dell\'utente')
      }
    }
  }

  const handleToggleStatus = async (userId: string, currentStatus: boolean) => {
    try {
      await toggleUserStatus({
        profileId: userId,
        active: !currentStatus
      })
    } catch (error) {
      console.error('Errore cambio stato utente:', error)
      alert('Errore durante il cambio di stato dell\'utente')
    }
  }

  const handleDeactivateApiKey = async (userId: string, userName: string) => {
    if (window.confirm(`Sei sicuro di voler disattivare l'API key di ${userName}?`)) {
      try {
        const result = await deactivateUserApiKey({ profileId: userId })
        if (result.success) {
          alert('API key disattivata con successo')
        } else {
          alert(result.message || 'Nessuna API key attiva trovata per questo utente')
        }
      } catch (error) {
        console.error('Errore disattivazione API key:', error)
        alert('Errore durante la disattivazione dell\'API key')
      }
    }
  }

  const handleCreateMockUser = async (e: React.FormEvent) => {
    e.preventDefault()
    if (mockSubmitting) return
    setMockFormError('')

    // Validate form
    if (!mockFormData.nome.trim() || !mockFormData.cognome.trim()) {
      setMockFormError('Nome e cognome sono obbligatori')
      return
    }

    if (!mockFormData.company_id) {
      setMockFormError('Seleziona una clinica')
      return
    }

    // Show alert explaining mock users
    const confirmed = window.confirm(
      '⚠️ ATTENZIONE: Utenti Mock\n\n' +
      'Stai per creare un utente "mock" che:\n' +
      '- Non può effettuare il login nel sistema\n' +
      '- Serve solo per creare prescrizioni\n' +
      '- Ha email generata automaticamente (il valore mostrato è indicativo)\n\n' +
      'Procedere con la creazione?'
    )

    if (!confirmed) return

    setMockSubmitting(true)
    try {
      const result = await createMockProfile({
        nome: mockFormData.nome.trim(),
        cognome: mockFormData.cognome.trim(),
        ruolo: mockFormData.ruolo,
        company_id: mockFormData.company_id as Id<"companies">
      })

      if (result.success) {
        alert(`Utente mock creato con successo!\nEmail: ${result.email}`)
        setShowMockForm(false)
        setMockFormData({
          nome: '',
          cognome: '',
          ruolo: 'dentista',
          company_id: ''
        })
      }
    } catch (error: unknown) {
      console.error('Errore creazione utente mock:', error)
      setMockFormError(readableMutationError(error))
    } finally {
      setMockSubmitting(false)
    }
  }

  const getRoleColor = (ruolo: string) => {
    if (ruolo === 'amministratore') return 'bg-purple-100 text-purple-800'
    if (ruolo === 'tecnico_lab') return 'bg-indigo-100 text-indigo-800'
    if (ruolo === 'clinic_owner') return 'bg-blue-100 text-blue-800'
    if (ruolo === 'dentista') return 'bg-green-100 text-green-800'
    if (ruolo === 'aso') return 'bg-yellow-100 text-yellow-800'
    if (ruolo === 'staff') return 'bg-gray-100 text-gray-800'
    return 'bg-gray-100 text-gray-800'
  }

  const getRoleLabel = (ruolo: string) => {
    const labels: Record<string, string> = {
      'amministratore': 'Amministratore',
      'tecnico_lab': 'Tecnico laboratorio',
      'clinic_owner': 'Titolare clinica',
      'dentista': 'Dentista',
      'aso': 'ASO',
      'staff': 'Personale clinica'
    }
    return labels[ruolo] || ruolo
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 border-b border-gray-200 pb-5 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex min-w-0 items-start gap-3">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Users className="h-5 w-5" aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <h1 className="text-2xl font-bold tracking-[-0.02em] text-gray-950">Gestione Utenti</h1>
            <p className="mt-1 text-sm leading-6 text-gray-600">
              Crea i profili, assegna cliniche e ruoli e controlla lo stato di accesso.
            </p>
          </div>
        </div>
        <div className="flex w-full flex-col-reverse gap-2 sm:w-auto sm:flex-row">
          {isDirectAdmin && (
            <Button
              type="button"
              variant="outline"
              disabled={mockSubmitting}
              onClick={() => setShowMockForm(!showMockForm)}
              className="w-full sm:w-auto"
              aria-expanded={showMockForm}
            >
              {showMockForm ? (
                <>
                  <X className="mr-2 h-4 w-4" aria-hidden="true" />
                  Chiudi mock
                </>
              ) : (
                <>
                  <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
                  Utente mock
                </>
              )}
            </Button>
          )}
          <Button asChild className="w-full sm:w-auto">
            <Link to="/users/new">
              <UserPlus className="mr-2 h-4 w-4" aria-hidden="true" />
              Nuovo utente
            </Link>
          </Button>
        </div>
      </div>

      {/* Nota informativa per utenti mock */}
      {isDirectAdmin && (
        <div className="flex items-start gap-3 rounded-md border border-orange-200 bg-orange-50 px-4 py-3 text-orange-900">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-orange-600" aria-hidden="true" />
          <p className="text-sm leading-6">
            <strong>Utenti Mock:</strong> possono essere utilizzati solo per creare prescrizioni,{' '}
            <strong>non possono effettuare l'accesso</strong> e vengono visualizzati con email @fastlab.it.
          </p>
        </div>
      )}

      {/* Form per creare utente mock */}
      {showMockForm && isDirectAdmin && (
        <Card className="border-orange-300 shadow-none">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center space-x-2 text-lg text-orange-700">
              <UserPlus className="h-5 w-5" aria-hidden="true" />
              <span>Crea Nuovo Utente Mock</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleCreateMockUser} className="space-y-4">
              {mockFormError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-md text-red-600 text-sm">
                  {mockFormError}
                </div>
              )}
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="mock-first-name" className="block text-sm font-medium text-gray-700 mb-1">
                    Nome *
                  </label>
                  <Input
                    id="mock-first-name"
                    type="text"
                    value={mockFormData.nome}
                    onChange={(e) => setMockFormData({...mockFormData, nome: e.target.value})}
                    placeholder="Inserisci il nome"
                    disabled={mockSubmitting}
                    required
                  />
                </div>
                
                <div>
                  <label htmlFor="mock-last-name" className="block text-sm font-medium text-gray-700 mb-1">
                    Cognome *
                  </label>
                  <Input
                    id="mock-last-name"
                    type="text"
                    value={mockFormData.cognome}
                    onChange={(e) => setMockFormData({...mockFormData, cognome: e.target.value})}
                    placeholder="Inserisci il cognome"
                    disabled={mockSubmitting}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="mock-role" className="block text-sm font-medium text-gray-700 mb-1">
                    Ruolo *
                  </label>
                  <select
                    id="mock-role"
                    value={mockFormData.ruolo}
                    onChange={(e) => setMockFormData({...mockFormData, ruolo: e.target.value as any})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-orange-500"
                    disabled={mockSubmitting}
                    required
                  >
                    <option value="dentista">Dentista</option>
                    <option value="clinic_owner">Titolare clinica</option>
                    <option value="aso">ASO</option>
                    <option value="staff">Personale clinica</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Il ruolo Amministratore non è disponibile per gli utenti mock
                  </p>
                </div>
                
                <div>
                  <label htmlFor="mock-company" className="block text-sm font-medium text-gray-700 mb-1">
                    Clinica *
                  </label>
                  <select
                    id="mock-company"
                    value={mockFormData.company_id}
                    onChange={(e) => setMockFormData({...mockFormData, company_id: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-orange-500"
                    disabled={mockSubmitting}
                    required
                  >
                    <option value="">Seleziona una clinica</option>
                    {companyOptions.map((company) => (
                      <option key={company._id} value={company._id}>
                        {company.nome}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="bg-gray-50 p-3 rounded-md">
                <p className="text-sm text-gray-600">
                  <strong>Email indicativa:</strong>{' '}
                  {mockFormData.nome && mockFormData.cognome 
                    ? `${mockFormData.nome.charAt(0).toLowerCase()}.${mockFormData.cognome.toLowerCase().replace(/\s+/g, '')}@fastlab.it`
                    : 'completa nome e cognome'
                  }
                </p>
              </div>

              <div className="flex flex-col gap-2 pt-2 sm:flex-row sm:items-center">
                <Button
                  type="submit"
                  disabled={mockSubmitting}
                  className="w-full sm:w-auto"
                >
                  {mockSubmitting ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                  ) : (
                    <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
                  )}
                  {mockSubmitting ? 'Creazione…' : 'Crea Utente Mock'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  disabled={mockSubmitting}
                  onClick={() => setShowMockForm(false)}
                  className="w-full sm:w-auto"
                >
                  Annulla
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Superficie operativa utenti */}
      <Card className="overflow-hidden shadow-none">
        <dl className="grid grid-cols-2 bg-gray-50/70 sm:grid-cols-4">
          <div className="border-b border-r border-gray-200 px-4 py-3 sm:border-b-0">
            <dt className="text-xs font-medium text-gray-500">Totale utenti</dt>
            <dd className="mt-0.5 text-xl font-semibold text-gray-950 tabular-nums">
              {(allUsers as any)?.length || 0}
            </dd>
          </div>
          <div className="border-b border-gray-200 px-4 py-3 sm:border-b-0 sm:border-r">
            <dt className="text-xs font-medium text-gray-500">Utenti attivi</dt>
            <dd className="mt-0.5 text-xl font-semibold text-gray-950 tabular-nums">
              {(allUsers as any)?.filter((u: any) => u.active && !u.is_deleted).length || 0}
            </dd>
          </div>
          <div className="border-r border-gray-200 px-4 py-3">
            <dt className="text-xs font-medium text-gray-500">Amministratori</dt>
            <dd className="mt-0.5 text-xl font-semibold text-gray-950 tabular-nums">
              {(allUsers as any)?.filter((u: any) => u.ruolo === 'amministratore').length || 0}
            </dd>
          </div>
          <div className="px-4 py-3">
            <dt className="text-xs font-medium text-gray-500">Utenti Mock</dt>
            <dd className="mt-0.5 text-xl font-semibold text-gray-950 tabular-nums">
              {(allUsers as any)?.filter((u: any) => isMockUser(u)).length || 0}
            </dd>
          </div>
        </dl>

        <div className="grid gap-3 border-y border-gray-200 px-4 py-4 sm:grid-cols-2 lg:grid-cols-[minmax(14rem,1fr)_minmax(12rem,0.62fr)_minmax(9rem,0.38fr)_minmax(11rem,0.48fr)_auto] lg:items-end">
          <div>
            <label htmlFor="user-search" className="mb-1.5 block text-xs font-medium text-gray-600">
              Cerca
            </label>
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" aria-hidden="true" />
              <Input
                id="user-search"
                placeholder="Nome, cognome o email"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          <div>
            <label htmlFor="user-company-filter" className="mb-1.5 block text-xs font-medium text-gray-600">
              Clinica
            </label>
            <select
              id="user-company-filter"
              value={companyFilter}
              onChange={(e) => setCompanyFilter(e.target.value)}
              disabled={companies === undefined}
              className="h-10 w-full rounded-md border border-gray-300 bg-white px-3 text-sm focus:outline-hidden focus:ring-2 focus:ring-primary disabled:cursor-not-allowed disabled:opacity-50"
            >
              <option value="">
                {companies === undefined ? 'Caricamento cliniche…' : 'Tutte le cliniche'}
              </option>
              {companyOptions.map((company) => (
                <option key={company._id} value={company._id}>
                  {company.nome}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="user-status-filter" className="mb-1.5 block text-xs font-medium text-gray-600">
              Stato
            </label>
            <select
              id="user-status-filter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="h-10 w-full rounded-md border border-gray-300 bg-white px-3 text-sm focus:outline-hidden focus:ring-2 focus:ring-primary"
            >
              <option value="">Tutti gli stati</option>
              <option value="active">Attivi</option>
              <option value="inactive">Inattivi</option>
            </select>
          </div>

          <div>
            <label htmlFor="user-role-filter" className="mb-1.5 block text-xs font-medium text-gray-600">
              Ruolo
            </label>
            <select
              id="user-role-filter"
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="h-10 w-full rounded-md border border-gray-300 bg-white px-3 text-sm focus:outline-hidden focus:ring-2 focus:ring-primary"
            >
              <option value="">Tutti i ruoli</option>
              <option value="amministratore">Amministratore</option>
              <option value="tecnico_lab">Tecnico laboratorio</option>
              <option value="clinic_owner">Titolare clinica</option>
              <option value="dentista">Dentista</option>
              <option value="aso">ASO</option>
              <option value="staff">Personale clinica</option>
            </select>
          </div>

          <p className="flex min-h-10 items-center text-sm text-gray-600 sm:col-span-2 lg:col-span-1 lg:justify-end" aria-live="polite">
            <span className="font-semibold text-gray-950 tabular-nums">{filteredUsers.length}</span>
            <span className="ml-1">risultati</span>
            {!hasActiveFilters && usersStatus === 'CanLoadMore' && (
              <span className="ml-1 text-gray-500">· altri disponibili</span>
            )}
          </p>
        </div>

        {filteredUsers.length === 0 ? (
          <div className="px-5 py-12 text-center">
            <Users className="mx-auto h-10 w-10 text-gray-400" aria-hidden="true" />
            <h3 className="mt-3 text-lg font-semibold text-gray-950">Nessun utente trovato</h3>
            <p className="mx-auto mt-1 max-w-lg text-sm leading-6 text-gray-600">
              {hasActiveFilters
                ? 'Prova a modificare i criteri di ricerca.'
                : 'Crea il primo profilo e assegnagli ruolo e cliniche.'}
            </p>
            {!hasActiveFilters && (
              <p className="mx-auto mt-3 max-w-lg text-sm text-gray-500">
                Al primo accesso con la stessa email, il profilo verrà collegato automaticamente ad Auth0.
              </p>
            )}
          </div>
        ) : (
          <table className="w-full border-collapse text-left">
            <caption className="sr-only">Utenti del sistema</caption>
            <thead className="hidden bg-gray-50 text-xs font-medium uppercase tracking-wide text-gray-500 lg:table-header-group">
              <tr>
                <th scope="col" className="w-[27%] px-4 py-3">Utente</th>
                <th scope="col" className="w-36 px-3 py-3">Ruolo</th>
                <th scope="col" className="px-3 py-3">Cliniche</th>
                <th scope="col" className="w-28 px-3 py-3">Stato</th>
                <th scope="col" className="w-40 px-3 py-3">Attività</th>
                <th scope="col" className="w-56 px-4 py-3 text-right">Azioni</th>
              </tr>
            </thead>
            <tbody className="block divide-y divide-gray-200 lg:table-row-group">
              {filteredUsers.map((user: any) => {
                const companyIds = getUserCompanyIds(user)
                const mockUser = isMockUser(user)
                const pendingAuth0User = isPendingAuth0User(user)
                const activeUser = user.active && !user.is_deleted

                return (
                  <tr
                    key={user._id}
                    className={`block transition-colors hover:bg-gray-50/70 lg:table-row ${
                      mockUser ? 'bg-orange-50/30' : 'bg-white'
                    }`}
                  >
                    <th scope="row" className="block px-4 pb-2 pt-4 text-left font-normal lg:table-cell lg:px-4 lg:py-3">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="break-words font-semibold leading-5 text-gray-950">
                            {user.nome} {user.cognome}
                          </span>
                          {mockUser && (
                            <Badge className="border border-orange-300 bg-orange-100 text-orange-800">
                              Mock
                            </Badge>
                          )}
                          {pendingAuth0User && (
                            <Badge className="border border-blue-200 bg-blue-50 text-blue-800">
                              Primo accesso atteso
                            </Badge>
                          )}
                        </div>
                        <p className="mt-1 break-all text-sm text-gray-600" title={user.email}>
                          {user.email}
                        </p>
                        {user.telefono && (
                          <p className="mt-0.5 text-xs text-gray-500">Tel. {user.telefono}</p>
                        )}
                        {mockUser && (
                          <p className="mt-1 text-xs font-medium text-orange-700">Non può effettuare il login</p>
                        )}
                        {pendingAuth0User && (
                          <p className="mt-1 text-xs font-medium text-blue-700">
                            Auth0 verrà collegato al primo accesso con questa email
                          </p>
                        )}
                      </div>
                    </th>

                    <td className="block px-4 py-1 lg:table-cell lg:px-3 lg:py-3">
                      <span className="mr-2 text-xs font-medium text-gray-500 lg:hidden">Ruolo</span>
                      <Badge className={getRoleColor(user.ruolo)}>
                        {getRoleLabel(user.ruolo)}
                      </Badge>
                    </td>

                    <td className="block px-4 py-2 lg:table-cell lg:px-3 lg:py-3">
                      <span className="mb-1.5 block text-xs font-medium text-gray-500 lg:hidden">Cliniche</span>
                      {companyIds.length > 0 ? (
                        <div className="flex flex-wrap gap-1.5">
                          {companyIds.map((companyId: string) => (
                            <span key={companyId} className="max-w-full break-words rounded-md bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
                              {companyLookup[companyId]?.nome ?? 'Clinica non disponibile'}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">—</span>
                      )}
                    </td>

                    <td className="block px-4 py-1 lg:table-cell lg:px-3 lg:py-3">
                      <span className="mr-2 text-xs font-medium text-gray-500 lg:hidden">Stato</span>
                      <Badge className={activeUser ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                        {activeUser ? 'Attivo' : 'Inattivo'}
                      </Badge>
                    </td>

                    <td className="block px-4 py-2 text-sm text-gray-600 lg:table-cell lg:px-3 lg:py-3">
                      <span className="mb-1 block text-xs font-medium text-gray-500 lg:hidden">Attività</span>
                      <p className="tabular-nums">
                        <span className="text-gray-500">Creato</span>{' '}
                        {new Date(user.created_at).toLocaleDateString('it-IT')}
                      </p>
                      {user.updated_at !== user.created_at && (
                        <p className="mt-0.5 tabular-nums">
                          <span className="text-gray-500">Aggiornato</span>{' '}
                          {new Date(user.updated_at).toLocaleDateString('it-IT')}
                        </p>
                      )}
                    </td>

                    <td className="block px-4 pb-4 pt-3 lg:table-cell lg:px-4 lg:py-3">
                      {canManageUsers && (
                        <div className="flex flex-wrap items-center gap-2 lg:justify-end">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => handleToggleStatus(user._id, user.active)}
                            className={`h-11 flex-1 px-3 lg:h-9 lg:w-9 lg:flex-none lg:px-0 ${
                              user.active
                                ? 'text-red-700 hover:text-red-800'
                                : 'text-green-700 hover:text-green-800'
                            }`}
                            aria-label={`${user.active ? 'Disattiva' : 'Riattiva'} ${user.nome} ${user.cognome}`}
                            title={user.active ? 'Disattiva utente' : 'Riattiva utente'}
                          >
                            {user.active ? (
                              <UserX className="h-4 w-4 lg:mr-0" aria-hidden="true" />
                            ) : (
                              <UserCheck className="h-4 w-4 lg:mr-0" aria-hidden="true" />
                            )}
                            <span className="ml-2 lg:sr-only">{user.active ? 'Disattiva' : 'Riattiva'}</span>
                          </Button>

                          {canManageApiKeys && (user.ruolo === 'amministratore' || user.ruolo === 'clinic_owner') && !mockUser && !pendingAuth0User && (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeactivateApiKey(user._id, `${user.nome} ${user.cognome}`)}
                              className="h-11 flex-1 px-3 text-orange-700 hover:text-orange-800 lg:h-9 lg:w-9 lg:flex-none lg:px-0"
                              title="Disattiva API Key"
                              aria-label={`Disattiva API key di ${user.nome} ${user.cognome}`}
                            >
                              <Key className="h-4 w-4" aria-hidden="true" />
                              <span className="ml-2 lg:sr-only">Disattiva API</span>
                            </Button>
                          )}

                          <Button
                            asChild
                            variant="outline"
                            size="sm"
                            className="h-11 flex-1 px-3 lg:h-9 lg:w-9 lg:flex-none lg:px-0"
                          >
                            <Link
                              to={`/users/${user._id}/edit`}
                              aria-label={`Modifica ${user.nome} ${user.cognome}`}
                              title="Modifica utente"
                            >
                              <Edit className="h-4 w-4" aria-hidden="true" />
                              <span className="ml-2 lg:sr-only">Modifica</span>
                            </Link>
                          </Button>

                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeleteUser(user._id)}
                            className="h-11 flex-1 px-3 text-red-700 hover:text-red-800 lg:h-9 lg:w-9 lg:flex-none lg:px-0"
                            aria-label={`Elimina ${user.nome} ${user.cognome}`}
                            title="Elimina utente"
                          >
                            <Trash2 className="h-4 w-4" aria-hidden="true" />
                            <span className="ml-2 lg:sr-only">Elimina</span>
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}

        {!hasActiveFilters && (usersStatus === 'CanLoadMore' || usersStatus === 'LoadingMore') && (
          <div className="flex justify-center border-t border-gray-200 px-4 py-4">
            <Button
              variant="outline"
              onClick={() => usersStatus === 'CanLoadMore' && loadMoreUsers(ITEMS_PER_PAGE)}
              disabled={usersStatus === 'LoadingMore'}
              className="min-h-11 w-full sm:w-auto sm:min-w-[200px]"
            >
              {usersStatus === 'LoadingMore' ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                <ChevronDown className="mr-2 h-4 w-4" aria-hidden="true" />
              )}
              {usersStatus === 'LoadingMore' ? 'Caricamento...' : 'Carica altri utenti'}
            </Button>
          </div>
        )}
      </Card>
    </div>
  )
}
